import React, { useState, useMemo } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    CircularProgress,
    Typography,
    useTheme,
    Paper,
} from "@mui/material";
import { tokens } from "../../../theme";
import { useCreateTechMutation } from "../../../features/techs/techsApiSlice";

const NewTechLoginForm = ({ open, onClose }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [createTech, { isLoading }] = useCreateTechMutation();

    // form state
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [password, setPassword] = useState("");
    const [sendInvite, setSendInvite] = useState(true);
    const [error, setError] = useState("");

    const gradientSx = useMemo(
        () => ({
            backgroundImage: `linear-gradient(135deg, ${colors.blueAccent[700]}, ${colors.primary[500]})`,
            borderRadius: 3,
            boxShadow: theme.shadows[6],
        }),
        [colors, theme.shadows]
    );

    const canSubmit =
        username.trim().length > 0 &&
        email.trim().length > 0 &&
        (sendInvite || password.trim().length > 0);

    const resetForm = () => {
        setUsername("");
        setEmail("");
        setName("");
        setPhone("");
        setPassword("");
        setSendInvite(true);
        setError("");
    };

    const handleClose = () => {
        if (!isLoading) {
            resetForm();
            onClose?.();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        try {
            const body = {
                username: username.trim(),
                email: email.trim(),
                name: name.trim() || undefined,
                phone: phone.trim() || undefined, // server may choose to store as phone_number
                sendInvitation: sendInvite,
            };

            if (!sendInvite && password.trim()) {
                body.permanentPassword = password.trim();
            }

            await createTech(body).unwrap();
            handleClose();
        } catch (err) {
            const msg =
                err?.data?.error ||
                err?.error ||
                err?.message ||
                "Failed to create tech user.";
            setError(String(msg));
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            keepMounted
            PaperProps={{            // ✅ use PaperProps instead of PaperComponent
                sx: {
                    backgroundImage: `linear-gradient(135deg, ${colors.blueAccent[700]}, ${colors.primary[500]})`,
                    borderRadius: 3,
                    boxShadow: theme.shadows[6],
                },
            }}
        >
            <DialogTitle sx={{ color: "#fff" }}>Create Tech Login</DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        mt: 1.5,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                    }}
                >
                    <TextField
                        label="Username"
                        required
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        fullWidth
                        autoComplete="username"               // ✅ helps browser & silences warning
                        InputProps={{ sx: { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" } }}
                        InputLabelProps={{ sx: { color: "#fff" } }}
                    />
                    <TextField
                        label="Email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        autoComplete="email"                  // ✅
                        InputProps={{ sx: { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" } }}
                        InputLabelProps={{ sx: { color: "#fff" } }}
                    />
                    <TextField
                        label="Name (optional)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        autoComplete="name"                   // ✅
                        InputProps={{ sx: { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" } }}
                        InputLabelProps={{ sx: { color: "#fff" } }}
                    />
                    <TextField
                        label="Phone (optional)"
                        placeholder="+1XXXXXXXXXX"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        fullWidth
                        autoComplete="tel"                    // ✅
                        InputProps={{ sx: { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" } }}
                        InputLabelProps={{ sx: { color: "#fff" } }}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        disabled={sendInvite}
                        autoComplete={sendInvite ? "off" : "new-password"}   // ✅ correct for set-permanent
                        helperText={
                            sendInvite
                                ? "Disabled when sending invitation."
                                : "Min 12 chars; include upper/lower/number/symbol if required."
                        }
                        InputProps={{ sx: { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" } }}
                        InputLabelProps={{ sx: { color: "#fff" } }}
                        sx={{ gridColumn: "1 / -1" }}
                    />

                    <Box gridColumn="1 / -1" display="flex" alignItems="center" justifyContent="space-between">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={sendInvite}
                                    onChange={(e) => setSendInvite(e.target.checked)}
                                />
                            }
                            label="Send invitation email (Cognito)"
                            sx={{ color: "#fff" }}
                        />
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                            {sendInvite
                                ? "Password will be set by the user."
                                : "Set a permanent password now."}
                        </Typography>
                    </Box>

                    <TextField
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        disabled={sendInvite}
                        helperText={sendInvite ? "Disabled when sending invitation." : "Min 12 chars; include upper/lower/number/symbol if your pool policy requires."}
                        InputProps={{ sx: { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" } }}
                        InputLabelProps={{ sx: { color: "#fff" } }}
                        sx={{ gridColumn: "1 / -1" }}
                    />

                    {error && (
                        <Typography sx={{ gridColumn: "1 / -1", color: "#ffb4b4" }}>
                            {error}
                        </Typography>
                    )}

                    <Box
                        gridColumn="1 / -1"
                        display="flex"
                        justifyContent="flex-end"
                        gap={1.5}
                        mt={1}
                    >
                        <Button onClick={handleClose} variant="outlined" disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!canSubmit || isLoading}
                        >
                            {isLoading ? <CircularProgress size={20} /> : "Create Tech"}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ pb: 2, px: 3 }} />
        </Dialog>
    );
};

export default NewTechLoginForm;
